import React from "react";
import {
  TableRow,
  TableCell,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Gavel as GavelIcon,
  Info as InfoIcon,
  MonetizationOn as MonetizationOnIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

import {
  formatearMonto,
  formatearRutDisplay,
  getBancoConfig,
} from "src/utils/conciliacionHelpers";
import {
  obtenerConfigBadgeScore,
  obtenerConfigBadgeRegla,
  tieneDiferenciaMonto,
  requiereGestionAbono,
  obtenerConfigBadgeDiferencia,
  obtenerDescripcionDiferencia,
  renderPreviewFacturasConEstado,
} from "../utils";
import { formatearFechaDisplay } from "src/utils/formatTime";
const useStyles = makeStyles((theme) => ({
  tableRow: {
    position: "relative",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionButton: {
    marginLeft: theme.spacing(0.5),
  },
  filaExito: {
    animation: "$fadeInScale 0.5s ease-out",
    backgroundColor: "rgba(76, 175, 80, 0.05)",
  },
  "@keyframes fadeInScale": {
    "0%": {
      opacity: 0,
      transform: "scale(0.95)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
  rowOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    borderRadius: 4,
  },
  overlayContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: "#fff",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
  },
}));

/**
 * Props del componente
 * @param {Object} detalle - Objeto con datos del movimiento y resultado de matching
 * @param {Boolean} showActions - Si muestra botones de acción
 * @param {String} categoria - Categoría del movimiento ('perfectos', 'buenos', etc.)
 * @param {Function} onVerSugerencias - Handler para ver sugerencias
 * @param {Function} onConciliarManual - Handler para conciliar manual
 * @param {Function} onConciliarIndividual - Handler para conciliar individual
 * @param {Boolean} procesandoAccion - Si hay una acción en curso
 * @param {Boolean} isProcessing - Si el movimiento está siendo procesado
 */
export default function FilaMovimiento({
  detalle,
  showActions,
  categoria,
  onVerSugerencias,
  onConciliarManual,
  onConciliarIndividual,
  procesandoAccion,
  isProcessing,
}) {
  const classes = useStyles();

  // Extraer datos del movimiento
  const movimiento = detalle.movimiento || {};
  const score = detalle.score || 0;
  const status = detalle.status;

  // Configuración del banco
  const bancoConfig = getBancoConfig(movimiento.banco);

  // Configuración del badge de score
  const scoreBadgeConfig = obtenerConfigBadgeScore(score);

  /**
   * Trunca texto largo
   */
  const truncarTexto = (texto, maxLength = 50) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  /**
   * Determina si el movimiento tiene facturas para conciliar
   */
  const tieneFacturas = detalle.facturas && detalle.facturas.length > 0;

  /**
   * Determina qué botones mostrar según categoría
   */
  const getBotonesAccion = () => {
    // Sin acciones si no está habilitado
    if (!showActions) return null;

    // Errores: sin acciones
    if (categoria === "errores") return null;

    // Sin match: solo conciliar manual
    if (categoria === "sinMatch" || !tieneFacturas) {
      return (
        <Tooltip title="Conciliar manualmente">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onConciliarManual(movimiento);
            }}
            disabled={procesandoAccion}
            className={classes.actionButton}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }

    // Perfectos, Buenos, Débiles: ver sugerencias + conciliar manual + conciliar individual
    return (
      <Box display="flex" justifyContent="center">
        {/* Ver Sugerencias */}
        <Tooltip title="Ver sugerencias de matching">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onVerSugerencias(movimiento);
            }}
            disabled={procesandoAccion}
            className={classes.actionButton}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Conciliar Manual */}
        <Tooltip title="Conciliar manualmente">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onConciliarManual(movimiento);
            }}
            disabled={procesandoAccion}
            className={classes.actionButton}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Conciliar Individual (solo si tiene facturas y es perfecto o bueno) */}
        {(categoria === "perfectos" || categoria === "buenos") &&
          tieneFacturas && (
            <Tooltip
              title={
                isProcessing ? "Conciliando..." : "Conciliar con sugerencia"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConciliarIndividual(detalle);
                  }}
                  disabled={procesandoAccion || isProcessing}
                  className={classes.actionButton}
                  style={{ color: isProcessing ? "#ccc" : "#4caf50" }}
                >
                  {isProcessing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
      </Box>
    );
  };

  return (
    <TableRow
      className={
        categoria === "recienConciliados" ? classes.filaExito : classes.tableRow
      }
      style={{ opacity: isProcessing ? 0.6 : 1, position: "relative" }}
    >
      {/* Overlay de carga */}
      {isProcessing && (
        <Box
          className={classes.rowOverlay}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Box className={classes.overlayContent}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Conciliando...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Fecha Movimiento */}
      <TableCell>
        <Typography variant="body2">
          {formatearFechaDisplay(movimiento.fecha_mov)}
        </Typography>
      </TableCell>

      {/* Banco */}
      <TableCell>
        <Chip
          label={bancoConfig.label}
          size="small"
          style={{
            backgroundColor: bancoConfig.bgColor,
            color: bancoConfig.color || "#000",
            fontWeight: 600,
            fontSize: 11,
          }}
        />
      </TableCell>

      {/* RUT Ordenante */}
      <TableCell>
        <Typography variant="body2">
          {formatearRutDisplay(movimiento.rut_ordenante)}
        </Typography>
        {movimiento.nombre_ordenante && (
          <Typography variant="caption" color="textSecondary" display="block">
            {truncarTexto(movimiento.nombre_ordenante, 30)}
          </Typography>
        )}
      </TableCell>

      {/* Glosa */}
      <TableCell>
        <Tooltip title={movimiento.glosa || ""} arrow>
          <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
            {truncarTexto(movimiento.glosa, 40)}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Monto */}
      <TableCell align="right">
        <Typography
          variant="body2"
          style={{
            fontWeight: 600,
            color: movimiento.tipo === "INGRESO" ? "#4CAF50" : "#F44336",
          }}
        >
          {formatearMonto(movimiento.monto)}
        </Typography>
      </TableCell>

      {/* Score */}
      <TableCell>
        <Box display="flex" flexDirection="column" gap={0.5}>
          {/* Badge de Score */}
          {score > 0 ? (
            <Chip
              label={`${score} pts - ${scoreBadgeConfig.label}`}
              size="small"
              style={{
                backgroundColor: scoreBadgeConfig.bgColor,
                color: scoreBadgeConfig.color,
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              N/A
            </Typography>
          )}

          {/* Badge de Status (condicional) */}
          {status === "AUTO_CONCILIADO" && (
            <Chip
              label="✓ Auto-conciliado"
              size="small"
              style={{
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                fontWeight: 500,
                fontSize: 10,
                height: 20,
              }}
            />
          )}

          {status === "ERROR" && (
            <Chip
              label="⚠ Error"
              size="small"
              style={{
                backgroundColor: "#ffebee",
                color: "#c62828",
                fontWeight: 500,
                fontSize: 10,
                height: 20,
              }}
            />
          )}

          {detalle.aplicada_por_regla &&
            (() => {
              const config = obtenerConfigBadgeRegla(
                detalle.aplicada_por_regla,
              );
              return (
                <Tooltip title={config.tooltip} arrow>
                  <Chip
                    icon={<GavelIcon style={{ fontSize: 12, color: "#fff" }} />}
                    label="Regla"
                    size="small"
                    style={{
                      backgroundColor: config.bgColor,
                      color: "#fff",
                      fontWeight: 500,
                      fontSize: 10,
                      height: 20,
                    }}
                  />
                </Tooltip>
              );
            })()}

          {detalle.requiere_validacion_humana && (
            <Tooltip
              title="Requiere validación humana antes de auto-conciliar"
              arrow
            >
              <Chip
                icon={<InfoIcon style={{ fontSize: 12, color: "#fff" }} />}
                label="Validar"
                size="small"
                style={{
                  backgroundColor: "#FF9800",
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: 10,
                  height: 20,
                }}
              />
            </Tooltip>
          )}

          {tieneDiferenciaMonto(detalle) &&
            (() => {
              const configDiferencia = obtenerConfigBadgeDiferencia(
                detalle.tipo_diferencia,
              );
              const descripcion = obtenerDescripcionDiferencia(detalle);
              const esAbono = requiereGestionAbono(detalle);

              return (
                <Tooltip title={descripcion} arrow>
                  <Chip
                    icon={
                      esAbono ? (
                        <MonetizationOnIcon
                          style={{ fontSize: 12, color: "#fff" }}
                        />
                      ) : (
                        <InfoIcon style={{ fontSize: 12, color: "#fff" }} />
                      )
                    }
                    label={
                      esAbono
                        ? `Abono: ${formatearMonto(detalle.monto_diferencia)}`
                        : configDiferencia.label
                    }
                    size="small"
                    style={{
                      backgroundColor: configDiferencia.bgColor,
                      color: configDiferencia.color,
                      fontWeight: 500,
                      fontSize: 10,
                      height: 20,
                    }}
                  />
                </Tooltip>
              );
            })()}

          {detalle.priorizado_por_monto_exacto && (
            <Tooltip title="Match priorizado por monto exacto" arrow>
              <Chip
                icon={
                  <MonetizationOnIcon style={{ fontSize: 12, color: "#fff" }} />
                }
                label="Exacto"
                size="small"
                style={{
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: 10,
                  height: 20,
                }}
              />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      {/* Facturas */}
      <TableCell>
        {(() => {
          const preview = renderPreviewFacturasConEstado(detalle);

          // Si es un string (sin facturas o 3+ facturas), mostrar como antes
          if (typeof preview === "string") {
            return (
              <Typography
                variant="caption"
                color={tieneFacturas ? "textPrimary" : "textSecondary"}
              >
                {preview}
              </Typography>
            );
          }

          // Si es un array (1-2 facturas), renderizar con badges
          if (Array.isArray(preview)) {
            return (
              <Box display="flex" flexDirection="column" gap={0.5}>
                {preview.map((factura, idx) => (
                  <Box key={idx} display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="textPrimary">
                      {factura.tipo} #{factura.folio}
                    </Typography>

                    {/* Badge ABONO */}
                    {factura.estado === "ABONO" && (
                      <Chip
                        label="ABONO"
                        size="small"
                        icon={
                          <MonetizationOnIcon
                            style={{ fontSize: 10, color: "#FFF" }}
                          />
                        }
                        style={{
                          backgroundColor: "#FF9800",
                          color: "#FFF",
                          fontSize: "0.65rem",
                          height: 16,
                          padding: "0 4px",
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            );
          }

          return null;
        })()}
      </TableCell>

      {/* Acciones */}
      {showActions && (
        <TableCell align="center">{getBotonesAccion()}</TableCell>
      )}
    </TableRow>
  );
}
